/**
 * Serac icon library — Lucide-compatible component signatures.
 *
 * Per design-system/SKILL.md §3.5: hand-authored line icons at
 * 1.4–1.75 stroke weight, never an external library. Every icon
 * accepts the same props as a Lucide component (className, strokeWidth,
 * aria-hidden, ref) so call sites that look like
 *
 *     <Icon icon={SECTION_ICONS.home} size="sm" />
 *
 * keep working without per-file edits.
 *
 * Adding a new icon: copy any existing one, swap the path, keep the
 * same viewBox/fill/stroke contract. Stroke weight 1.75 unless the
 * glyph reads better filled.
 */

import { forwardRef, type SVGProps } from 'react';

// Lucide-compatible Props: also accepts `size` (number/string) which
// sets both width and height. Lets Serac icons drop into call sites
// that originally targeted Lucide.
export type SeracIconProps = Omit<SVGProps<SVGSVGElement>, 'size'> & {
  size?: number | string;
  strokeWidth?: number | string;
};

const STROKE = 1.75;

const seracIcon = (paths: React.ReactNode, opts?: { fill?: 'none' | 'currentColor' }) =>
  forwardRef<SVGSVGElement, SeracIconProps>(({ size, width, height, strokeWidth, ...rest }, ref) => (
    <svg
      ref={ref}
      width={width ?? size ?? '1em'}
      height={height ?? size ?? '1em'}
      viewBox="0 0 24 24"
      fill={opts?.fill ?? 'none'}
      stroke={opts?.fill === 'currentColor' ? 'none' : 'currentColor'}
      strokeWidth={strokeWidth ?? STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={true}
      {...rest}
    >
      {paths}
    </svg>
  ));

/* ============================================================
   Core — navigation, structure, surfaces
   ============================================================ */
export const SrHome = seracIcon(
  <path d="M3 11l9-8 9 8v9a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2v-9z" />,
);
export const SrLayers = seracIcon(
  <path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 18l9 5 9-5" />,
);
export const SrBox = seracIcon(
  <>
    <path d="M3 7l9-4 9 4v10l-9 4-9-4V7z" />
    <path d="M3 7l9 4 9-4M12 11v10" />
  </>,
);
export const SrFolder = seracIcon(
  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />,
);
export const SrPackage = seracIcon(
  <>
    <path d="M3 7l9-4 9 4-9 4-9-4z" />
    <path d="M3 7v10l9 4 9-4V7M12 11v10" />
  </>,
);
export const SrBook = seracIcon(
  <>
    <path d="M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4z" />
    <path d="M4 17a3 3 0 0 1 3-3h11" />
  </>,
);

/* ============================================================
   Communication / activity
   ============================================================ */
export const SrMessage = seracIcon(
  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
);
export const SrTerminal = seracIcon(
  <>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 9l3 3-3 3M13 15h4" />
  </>,
);
export const SrActivity = seracIcon(
  <path d="M3 12h4l3-9 4 18 3-9h4" />,
);
export const SrBell = seracIcon(
  <>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10 21a2 2 0 0 0 4 0" />
  </>,
);
export const SrMail = seracIcon(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </>,
);
export const SrInbox = seracIcon(
  <>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5 5l-3 7v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3-7a2 2 0 0 0-2-1H7a2 2 0 0 0-2 1z" />
  </>,
);

/* ============================================================
   People / identity
   ============================================================ */
export const SrUser = seracIcon(
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
  </>,
);
export const SrUsers = seracIcon(
  <>
    <circle cx="9" cy="8" r="3" />
    <circle cx="17" cy="9" r="2" />
    <path d="M3 19c0-3 3-5 6-5s6 2 6 5M14 19c0-2 2-3.5 4-3.5s4 1.5 4 3.5" />
  </>,
);
export const SrBuilding = seracIcon(
  <path d="M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M16 8h2a2 2 0 0 1 2 2v11M4 21h16M8 7h2M8 11h2M8 15h2M8 19h2" />,
);

/* ============================================================
   Status / chart / data
   ============================================================ */
export const SrChart = seracIcon(
  <path d="M3 21h18M6 17V9M11 17V5M16 17v-7M21 17v-4" />,
);
export const SrChartUp = seracIcon(
  <>
    <path d="M3 17l6-6 4 4 7-7" />
    <path d="M14 8h6v6" />
  </>,
);
export const SrClock = seracIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);
export const SrEye = seracIcon(
  <>
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);

/* ============================================================
   Security / keys / shields
   ============================================================ */
export const SrKey = seracIcon(
  <>
    <circle cx="8" cy="15" r="4" />
    <path d="M11 12l9-9M16 7l3 3" />
  </>,
);
export const SrLock = seracIcon(
  <>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>,
);
export const SrShield = seracIcon(
  <path d="M12 3l8 3v6c0 4.5-3.3 8.5-8 9-4.7-.5-8-4.5-8-9V6l8-3z" />,
);
export const SrShieldCheck = seracIcon(
  <>
    <path d="M12 3l8 3v6c0 4.5-3.3 8.5-8 9-4.7-.5-8-4.5-8-9V6l8-3z" />
    <path d="M9 12l2 2 4-4" />
  </>,
);

/* ============================================================
   Action / control
   ============================================================ */
export const SrSettings = seracIcon(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.82-.33 1.7 1.7 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .33-1.82 1.7 1.7 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09c.02.68.4 1.29 1 1.51a1.7 1.7 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.22.6.83.98 1.51 1H21a2 2 0 1 1 0 4h-.09c-.68.02-1.29.4-1.51 1z" />
  </>,
);
export const SrSearch = seracIcon(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </>,
);
export const SrCopy = seracIcon(
  <>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
  </>,
);
export const SrCheck = seracIcon(
  <path d="M5 12.5l5 5 9-11" />,
);
export const SrX = seracIcon(
  <path d="M6 6l12 12M18 6L6 18" />,
);
export const SrPlus = seracIcon(
  <path d="M12 5v14M5 12h14" />,
);
export const SrEdit = seracIcon(
  <>
    <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </>,
);
export const SrArrow = seracIcon(
  <path d="M5 12h14M13 6l6 6-6 6" />,
);
export const SrArrowUp = seracIcon(
  <path d="M12 19V5M5 12l7-7 7 7" />,
);
export const SrChev = seracIcon(
  <path d="M9 6l6 6-6 6" />,
);
export const SrChevDown = seracIcon(
  <path d="M6 9l6 6 6-6" />,
);
export const SrExternal = seracIcon(
  <path d="M14 4h6v6M10 14L20 4M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />,
);

/* ============================================================
   Domain — Serac product surfaces
   ============================================================ */
export const SrBolt = seracIcon(<path d="M13 2L3 14h7l-1 8 10-12h-7z" />, { fill: 'currentColor' });
export const SrGit = seracIcon(
  <>
    <circle cx="6" cy="6" r="2" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="18" cy="12" r="2" />
    <path d="M6 8v8M6 12c0-3 2-6 6-6h4M12 6v0" />
  </>,
);
export const SrLink = seracIcon(
  <>
    <path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
    <path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
  </>,
);
export const SrGlobe = seracIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </>,
);
export const SrCard = seracIcon(
  <>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18" />
  </>,
);
export const SrReceipt = seracIcon(
  <>
    <path d="M5 3v18l3-2 3 2 3-2 3 2 3-2V3z" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </>,
);
export const SrWallet = seracIcon(
  <>
    <path d="M3 8a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <circle cx="17" cy="13" r="1.5" fill="currentColor" />
  </>,
);
export const SrTag = seracIcon(
  <>
    <path d="M2 12V4a2 2 0 0 1 2-2h8l10 10-10 10z" />
    <circle cx="7.5" cy="7.5" r="1.25" fill="currentColor" />
  </>,
);
export const SrPalette = seracIcon(
  <>
    <path d="M12 3a9 9 0 1 0 0 18c1 0 2-1 2-2s-1-1.5-1-3 1-2 2-2h2a4 4 0 0 0 4-4 9 9 0 0 0-9-7z" />
    <circle cx="7.5" cy="11" r="1.25" fill="currentColor" />
    <circle cx="11" cy="7" r="1.25" fill="currentColor" />
    <circle cx="16" cy="9" r="1.25" fill="currentColor" />
  </>,
);
export const SrFile = seracIcon(
  <>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
  </>,
);
export const SrClipboard = seracIcon(
  <>
    <rect x="6" y="4" width="12" height="18" rx="2" />
    <path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" />
    <path d="M9 11h6M9 15h6" />
  </>,
);
export const SrSnowflake = seracIcon(
  <path d="M12 2v20M2 12h20M5 5l14 14M19 5L5 19M12 2l-2 3M12 2l2 3M12 22l-2-3M12 22l2-3M2 12l3-2M2 12l3 2M22 12l-3-2M22 12l-3 2" />,
);
export const SrSupport = seracIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4" />
    <path d="M5 5l3.5 3.5M19 5l-3.5 3.5M5 19l3.5-3.5M19 19l-3.5-3.5" />
  </>,
);
export const SrPlug = seracIcon(
  <>
    <path d="M9 4v4M15 4v4" />
    <path d="M6 8h12v4a6 6 0 0 1-12 0V8z" />
    <path d="M12 18v4" />
  </>,
);
export const SrLightbulb = seracIcon(
  <>
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.5V18h8v-3.5A7 7 0 0 0 12 2z" />
  </>,
);
export const SrRocket = seracIcon(
  <path d="M5 19s-1 0-1-1c0-3 6-12 8-12 1 0 4 3 4 4 0 2-9 8-12 8a1 1 0 0 1-1-1zM14 6l4 4M9 13l2 2M16 17a4 4 0 0 1 4-4M4 16a4 4 0 0 0-4 4" />,
);
export const SrImage = seracIcon(
  <>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5-9 9" />
  </>,
);
export const SrColumns = seracIcon(
  <>
    <rect x="3" y="3" width="6" height="18" rx="1" />
    <rect x="10" y="3" width="4" height="18" rx="1" />
    <rect x="15" y="3" width="6" height="18" rx="1" />
  </>,
);
export const SrFileSignature = seracIcon(
  <>
    <path d="M15 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M15 3v5h5" />
    <path d="M8 17l3-3 1 1 3-3" />
  </>,
);
export const SrBot = seracIcon(
  <>
    <rect x="4" y="7" width="16" height="12" rx="3" />
    <path d="M12 3v4" />
    <circle cx="9" cy="13" r="1.25" fill="currentColor" />
    <circle cx="15" cy="13" r="1.25" fill="currentColor" />
  </>,
);

/* ============================================================
   Lucide-compatible aliases — every name a portal file imports.
   Hand-authored Serac icons where they exist; Lucide passthrough
   with Serac stroke (1.75) for the long-tail, so EVERYTHING shares
   the same line weight regardless of source.

   Future rule: NEVER import directly from 'lucide-react'. Always
   import from this module. New icons go above (hand-authored) when
   they sit on visible chrome (sidebar / topbar / primary buttons),
   or as passthroughs below for one-off / data-classification icons.
   ============================================================ */

import {
  Activity as LcActivity, AlertCircle as LcAlertCircle, AlertOctagon as LcAlertOctagon,
  AlertTriangle as LcAlertTriangle, ArrowDown as LcArrowDown,
  ArrowDownWideNarrow as LcArrowDownWideNarrow, ArrowLeft as LcArrowLeft,
  ArrowRight as LcArrowRight, ArrowRightLeft as LcArrowRightLeft,
  ArrowUpNarrowWide as LcArrowUpNarrowWide, Asterisk as LcAsterisk,
  AsteriskSquare as LcAsteriskSquare, Ban as LcBan, BarChart3 as LcBarChart3,
  BookOpen as LcBookOpen, BrainCircuit as LcBrainCircuit,
  CheckCircle as LcCheckCircle, CheckCircle2 as LcCheckCircle2,
  ChevronUp as LcChevronUp, CircleCheck as LcCircleCheck, CircleX as LcCircleX,
  ClipboardCheck as LcClipboardCheck, ClipboardList as LcClipboardList,
  Code as LcCode, Code2 as LcCode2, CornerDownLeft as LcCornerDownLeft,
  Cpu as LcCpu, Database as LcDatabase, DollarSign as LcDollarSign,
  Download as LcDownload, EyeOff as LcEyeOff, FileCheck as LcFileCheck,
  FileCode2 as LcFileCode2, FileSpreadsheet as LcFileSpreadsheet,
  FileText as LcFileText, FileX as LcFileX, GitBranch as LcGitBranch,
  GitCompareArrows as LcGitCompareArrows, Github as LcGithub,
  HelpCircle as LcHelpCircle, Image as LcImage, Info as LcInfo,
  Keyboard as LcKeyboard, LayoutGrid as LcLayoutGrid,
  LayoutTemplate as LcLayoutTemplate, Link2 as LcLink2,
  ListChecks as LcListChecks, Loader as LcLoader, Loader2 as LcLoader2,
  LoaderCircle as LcLoaderCircle, Maximize2 as LcMaximize2,
  MessageCircle as LcMessageCircle, Minimize2 as LcMinimize2, Minus as LcMinus,
  Monitor as LcMonitor, Moon as LcMoon, Network as LcNetwork,
  PanelLeft as LcPanelLeft, PanelLeftClose as LcPanelLeftClose,
  Paperclip as LcPaperclip, Play as LcPlay, PlayCircle as LcPlayCircle,
  Plug as LcPlug, Puzzle as LcPuzzle, Radar as LcRadar,
  RefreshCcw as LcRefreshCcw, RefreshCw as LcRefreshCw, Repeat as LcRepeat,
  RotateCcw as LcRotateCcw, Save as LcSave, Send as LcSend,
  Server as LcServer, ShieldAlert as LcShieldAlert,
  ShoppingCart as LcShoppingCart, SlashSquare as LcSlashSquare,
  Smartphone as LcSmartphone, Sparkles as LcSparkles, Store as LcStore,
  Sun as LcSun, Tablet as LcTablet, Type as LcType, Upload as LcUpload,
  UserPlus as LcUserPlus, Wand2 as LcWand2, Workflow as LcWorkflow,
  Wrench as LcWrench, XCircle as LcXCircle, Zap as LcZap,
  type LucideIcon, type LucideProps,
} from 'lucide-react';

// Wrap a Lucide component so it always gets Serac defaults.
const seracLucide = (Icon: LucideIcon) =>
  forwardRef<SVGSVGElement, LucideProps>((props, ref) => (
    <Icon ref={ref} strokeWidth={STROKE} {...props} />
  ));

/* Hand-authored Serac aliases (Lucide naming) */
export const Home = SrHome;
export const Activity = seracLucide(LcActivity);
export const Layers = SrLayers;
export const Box = SrBox;
export const Folder = SrFolder;
export const Package = SrPackage;
export const BookMarked = SrBook;
export const BookOpen = seracLucide(LcBookOpen);
export const MessageSquare = SrMessage;
export const Terminal = SrTerminal;
export const Bell = SrBell;
export const Mail = SrMail;
export const Inbox = SrInbox;
export const User = SrUser;
export const Users = SrUsers;
export const Building2 = SrBuilding;
export const BarChart2 = SrChart;
export const TrendingUp = SrChartUp;
export const Clock = SrClock;
export const Eye = SrEye;
export const Key = SrKey;
export const Lock = SrLock;
export const Shield = SrShield;
export const ShieldCheck = SrShieldCheck;
export const Settings = SrSettings;
export const Search = SrSearch;
export const Copy = SrCopy;
export const Check = SrCheck;
export const X = SrX;
export const Plus = SrPlus;
export const Edit = SrEdit;
export const ArrowUp = SrArrowUp;
export const ChevronRight = SrChev;
export const ChevronDown = SrChevDown;
export const ExternalLink = SrExternal;
export const Globe = SrGlobe;
export const CreditCard = SrCard;
export const Receipt = SrReceipt;
export const Wallet = SrWallet;
export const Tag = SrTag;
export const Palette = SrPalette;
export const FileText = SrFile;
export const ClipboardList = seracLucide(LcClipboardList);
export const Snowflake = SrSnowflake;
export const LifeBuoy = SrSupport;
export const Lightbulb = SrLightbulb;
export const Rocket = SrRocket;
export const ImageIcon = SrImage;
export const Image = SrImage;
export const Columns3 = SrColumns;
export const FileSignature = SrFileSignature;
export const Bot = SrBot;
// Earlier this aliased to LcGitBranch with the comment "used as nav link
// icon", but that meant SECTION_ICONS.link rendered the same Y-shape as
// SECTION_ICONS.git — three sidebar items showed identical icons. Use
// the real chain glyph (Link2 — Lucide's cleaner two-link variant) so
// `link` and `git` actually look different.
export const Link = seracLucide(LcLink2);
export const Plug = seracLucide(LcPlug);
export const PanelLeft = seracLucide(LcPanelLeft);
export const PanelLeftClose = seracLucide(LcPanelLeftClose);

/* Long-tail Lucide passthroughs (Serac stroke applied) */
export const AlertCircle = seracLucide(LcAlertCircle);
export const AlertOctagon = seracLucide(LcAlertOctagon);
export const AlertTriangle = seracLucide(LcAlertTriangle);
export const ArrowDown = seracLucide(LcArrowDown);
export const ArrowDownWideNarrow = seracLucide(LcArrowDownWideNarrow);
export const ArrowLeft = seracLucide(LcArrowLeft);
export const ArrowRight = seracLucide(LcArrowRight);
export const ArrowRightLeft = seracLucide(LcArrowRightLeft);
export const ArrowUpNarrowWide = seracLucide(LcArrowUpNarrowWide);
export const Asterisk = seracLucide(LcAsterisk);
export const AsteriskSquare = seracLucide(LcAsteriskSquare);
export const Ban = seracLucide(LcBan);
export const BarChart3 = seracLucide(LcBarChart3);
export const BrainCircuit = seracLucide(LcBrainCircuit);
export const CheckCircle = seracLucide(LcCheckCircle);
export const CheckCircle2 = seracLucide(LcCheckCircle2);
export const ChevronUp = seracLucide(LcChevronUp);
export const CircleCheck = seracLucide(LcCircleCheck);
export const CircleX = seracLucide(LcCircleX);
export const ClipboardCheck = seracLucide(LcClipboardCheck);
export const Code = seracLucide(LcCode);
export const Code2 = seracLucide(LcCode2);
export const CornerDownLeft = seracLucide(LcCornerDownLeft);
export const Cpu = seracLucide(LcCpu);
export const Database = seracLucide(LcDatabase);
export const DollarSign = seracLucide(LcDollarSign);
export const Download = seracLucide(LcDownload);
export const EyeOff = seracLucide(LcEyeOff);
export const FileCheck = seracLucide(LcFileCheck);
export const FileCode2 = seracLucide(LcFileCode2);
export const FileSpreadsheet = seracLucide(LcFileSpreadsheet);
export const FileX = seracLucide(LcFileX);
export const GitBranch = seracLucide(LcGitBranch);
export const GitCompareArrows = seracLucide(LcGitCompareArrows);
export const Github = seracLucide(LcGithub);
export const HelpCircle = seracLucide(LcHelpCircle);
export const Info = seracLucide(LcInfo);
export const Keyboard = seracLucide(LcKeyboard);
export const LayoutGrid = seracLucide(LcLayoutGrid);
export const LayoutTemplate = seracLucide(LcLayoutTemplate);
export const Link2 = seracLucide(LcLink2);
export const ListChecks = seracLucide(LcListChecks);
export const Loader = seracLucide(LcLoader);
export const Loader2 = seracLucide(LcLoader2);
export const LoaderCircle = seracLucide(LcLoaderCircle);
export const Maximize2 = seracLucide(LcMaximize2);
export const MessageCircle = seracLucide(LcMessageCircle);
export const Minimize2 = seracLucide(LcMinimize2);
export const Minus = seracLucide(LcMinus);
export const Monitor = seracLucide(LcMonitor);
export const Moon = seracLucide(LcMoon);
export const Network = seracLucide(LcNetwork);
export const Paperclip = seracLucide(LcPaperclip);
export const Play = seracLucide(LcPlay);
export const PlayCircle = seracLucide(LcPlayCircle);
export const Puzzle = seracLucide(LcPuzzle);
export const Radar = seracLucide(LcRadar);
export const RefreshCcw = seracLucide(LcRefreshCcw);
export const RefreshCw = seracLucide(LcRefreshCw);
export const Repeat = seracLucide(LcRepeat);
export const RotateCcw = seracLucide(LcRotateCcw);
export const RotateCw = seracLucide(LcRefreshCw);
export const Save = seracLucide(LcSave);
export const Send = seracLucide(LcSend);
export const Server = seracLucide(LcServer);
export const ShieldAlert = seracLucide(LcShieldAlert);
export const ShoppingCart = seracLucide(LcShoppingCart);
export const SlashSquare = seracLucide(LcSlashSquare);
export const Smartphone = seracLucide(LcSmartphone);
export const Sparkles = seracLucide(LcSparkles);
export const Store = seracLucide(LcStore);
export const Sun = seracLucide(LcSun);
export const Tablet = seracLucide(LcTablet);
export const Type = seracLucide(LcType);
export const Upload = seracLucide(LcUpload);
export const UserPlus = seracLucide(LcUserPlus);
export const Wand2 = seracLucide(LcWand2);
export const Workflow = seracLucide(LcWorkflow);
export const Wrench = seracLucide(LcWrench);
export const XCircle = seracLucide(LcXCircle);
export const Zap = seracLucide(LcZap);

/* Re-export Lucide types for consumers that need them */
export type { LucideIcon, LucideProps };

/* Late additions — surfaced by typecheck after sweep */
import {
  Boxes as LcBoxes, KeyRound as LcKeyRound, Settings2 as LcSettings2,
  FileCheck2 as LcFileCheck2,
} from 'lucide-react';
export const Boxes = seracLucide(LcBoxes);
export const KeyRound = seracLucide(LcKeyRound);
export const Settings2 = seracLucide(LcSettings2);
export const FileCheck2 = seracLucide(LcFileCheck2);

/* Round 2 missing icons */
import {
  Recycle as LcRecycle, ScanSearch as LcScanSearch, BookTemplate as LcBookTemplate,
  FileCode as LcFileCode, Circle as LcCircle, ScrollText as LcScrollText,
  Archive as LcArchive,
} from 'lucide-react';
export const Recycle = seracLucide(LcRecycle);
export const ScanSearch = seracLucide(LcScanSearch);
export const BookTemplate = seracLucide(LcBookTemplate);
export const FileCode = seracLucide(LcFileCode);
export const Circle = seracLucide(LcCircle);
export const ScrollText = seracLucide(LcScrollText);
export const Archive = seracLucide(LcArchive);

/* Round 3 — provider/source/type icons used by utils/icons.tsx */
import {
  Atom as LcAtom, Bug as LcBug, CheckSquare as LcCheckSquare,
  Cherry as LcCherry, CircleDot as LcCircleDot, CircleDotDashed as LcCircleDotDashed,
  Clapperboard as LcClapperboard, Cloud as LcCloud, FileEdit as LcFileEdit,
  Flame as LcFlame, HandHelping as LcHandHelping, Handshake as LcHandshake,
  HardHat as LcHardHat, Hash as LcHash, LetterText as LcLetterText,
  Ruler as LcRuler, Scroll as LcScroll, Shuffle as LcShuffle,
  SmilePlus as LcSmilePlus, Square as LcSquare, Table2 as LcTable2,
  Target as LcTarget, Telescope as LcTelescope, Wheat as LcWheat,
  Wind as LcWind,
} from 'lucide-react';
export const Atom = seracLucide(LcAtom);
export const Bug = seracLucide(LcBug);
export const CheckSquare = seracLucide(LcCheckSquare);
export const Cherry = seracLucide(LcCherry);
export const CircleDot = seracLucide(LcCircleDot);
export const CircleDotDashed = seracLucide(LcCircleDotDashed);
export const Clapperboard = seracLucide(LcClapperboard);
export const Cloud = seracLucide(LcCloud);
export const FileEdit = seracLucide(LcFileEdit);
export const Flame = seracLucide(LcFlame);
export const HandHelping = seracLucide(LcHandHelping);
export const Handshake = seracLucide(LcHandshake);
export const HardHat = seracLucide(LcHardHat);
export const Hash = seracLucide(LcHash);
export const LetterText = seracLucide(LcLetterText);
export const Ruler = seracLucide(LcRuler);
export const Scroll = seracLucide(LcScroll);
export const Shuffle = seracLucide(LcShuffle);
export const SmilePlus = seracLucide(LcSmilePlus);
export const Square = seracLucide(LcSquare);
export const Table2 = seracLucide(LcTable2);
export const Target = seracLucide(LcTarget);
export const Telescope = seracLucide(LcTelescope);
export const Wheat = seracLucide(LcWheat);
export const Wind = seracLucide(LcWind);

/* ============================================================
   Service-integration brand marks
   ──────────────────────────────────────────────────────────
   Hand-authored line-style abstractions of the integration
   brand marks. Per design-system/Components.html § 10:
     "single line-icon set, 24×24 viewBox, stroke 1.75,
      currentColor — don't import Lucide ad hoc."
   These replace the previous SOURCE_ICONS mapping that paired
   service ids to mismatched Lucide shapes (Jira → ClipboardList,
   Confluence → BookOpen, ServiceNow → Snowflake, etc.).
   They are deliberately abstracted, not pixel-exact brand logos —
   the goal is consistency across rows of integrations, not legal-
   department-perfect reproduction. Add new services here.
   ============================================================ */

// ServiceNow — circle with three flowing curves (the "Now" flow theme).
export const SrServicenow = seracIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M6.5 9.5c1.5 1 3.5-1 5.5 0s4 -1 5.5 0" />
    <path d="M6.5 12c1.5 1 3.5-1 5.5 0s4 -1 5.5 0" />
    <path d="M6.5 14.5c1.5 1 3.5-1 5.5 0s4 -1 5.5 0" />
  </>,
);

// Jira — two stacked diamonds (Atlassian Jira "stacked papers" mark).
export const SrJira = seracIcon(
  <>
    <path d="M12 3 L18 9 L12 15 L6 9 Z" />
    <path d="M12 9 L17 14 L12 19 L7 14 Z" />
  </>,
);

// Confluence — paired curves (the C-curve Atlassian Confluence shape).
export const SrConfluence = seracIcon(
  <>
    <path d="M5 8c1.5-2 4-3 7-2s5 3 7 2" />
    <path d="M5 16c1.5 2 4 3 7 2s5-3 7-2" />
  </>,
);

// GitLab — three triangles fanning (the tanuki simplified to peaks).
export const SrGitlab = seracIcon(
  <>
    <path d="M12 3 L9 11 L15 11 Z" />
    <path d="M3 11 L9 11 L7 17 Z" />
    <path d="M21 11 L15 11 L17 17 Z" />
    <path d="M7 17 L12 21 L17 17" />
  </>,
);

// Azure DevOps — infinity loop (∞) suggesting the DevOps cycle.
export const SrAzureDevops = seracIcon(
  <path d="M8 15a3 3 0 1 1 0-6c2.5 0 4 6 8 6a3 3 0 0 0 0-6c-2.5 0-4 6-8 6z" />,
);
